import { useQuery } from '@apollo/client'
import { Link, Navigate } from 'react-router-dom'
import { Loader } from '../../components'
import { Avatar } from '../../elements'
import { GET_CAMPAIGN_BY_UUID, GetCampaignByUuidResponse } from '../../gql'
import { QueryGetCampaignByUuidArgs } from '../../gql/types'
import { CampaignApplicantsPublic } from './CampaignApplicantsPublic'
import { CampaignContentPublic } from './CampaignContentPublic'
import { Header } from './components/Header'

export const CampaignPublic = ({
  view,
  uuid,
}: {
  view: string
  uuid: string
}) => {
  // Basic campaign info query to show header
  const { loading, error, data } = useQuery<
    GetCampaignByUuidResponse,
    QueryGetCampaignByUuidArgs
  >(GET_CAMPAIGN_BY_UUID, {
    variables: {
      uuid,
    },
  })

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  if (loading) {
    return <Loader />
  }

  if (!loading && !data?.getCampaignByUuid) {
    return <p>Campaign does not exist.</p>
  }

  const campaign = data?.getCampaignByUuid

  // Validate view parameter
  if (view !== 'applicants' && view !== 'content') {
    // Redirect to applicants view by default
    return <Navigate to={`/campaigns/${uuid}/applicants`} replace />
  }

  return (
    <div className='min-h-screen bg-gray-50 pb-10'>
      {/* Header/Navbar */}
      <Header />

      {/* Main content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6'>
        <div className='bg-white rounded-lg shadow overflow-hidden'>
          {/* Campaign info header (shared between both views) */}
          <div className='flex flex-col px-6 border-b border-gray-200'>
            <div className='grid grid-cols-1 lg:flex lg:flex-row lg:justify-between py-2'>
              <div className='col-span-1'>
                <div className='flex items-start'>
                  {campaign?.brand?.logo?.url && (
                    <div className='mr-3'>
                      <Avatar
                        src={campaign.brand.logo.url}
                        alt={campaign.brand.name || 'Brand logo'}
                        type='brand'
                        size='sm'
                        className='h-12 w-12'
                      />
                    </div>
                  )}
                  <div>
                    <div className='font-bold text-3xl font-outfit mt-1'>
                      {campaign?.title}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation tabs */}
              <div className='flex items-center mt-4 lg:mt-0 space-x-4'>
                <Link
                  to={`/campaigns/${uuid}/applicants`}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    view === 'applicants'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Applicants
                </Link>
                <Link
                  to={`/campaigns/${uuid}/content`}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    view === 'content'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Content
                </Link>
              </div>
            </div>
          </div>

          {/* Render appropriate view based on the URL parameter */}
          {view === 'applicants' ? (
            <CampaignApplicantsPublic campaignUuid={uuid} campaign={campaign} />
          ) : (
            <CampaignContentPublic campaignUuid={uuid} campaign={campaign} />
          )}
        </div>
      </div>
    </div>
  )
}
